import type { FC, FormEvent, ReactNode } from 'react'

import { useId, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, Check, LoaderCircle } from 'lucide-react'
import { LINKS } from '@/constants/site'

type Status = 'idle' | 'sending' | 'sent' | 'error'

/** A one-field signup for the blog's article updates. */
export const NewsletterForm: FC = (): ReactNode => {
    const router = useRouter()
    const id = useId()
    const [email, setEmail] = useState('')
    const [trap, setTrap] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState<string | null>(null)

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            setStatus('error')
            setMessage('Skriv inn en gyldig e-postadresse.')
            return
        }

        setStatus('sending')
        setMessage(null)

        try {
            const response = await fetch('/api/newsletter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, website: trap, sourcePage: router.asPath })
            })

            const result = await response.json().catch(() => null) as { ok?: boolean, message?: string } | null

            if (!response.ok || !result?.ok) {
                setStatus('error')
                setMessage(result?.message ?? 'Noe gikk galt. Prøv igjen om litt.')
                return
            }

            setStatus('sent')
        }
        catch {
            setStatus('error')
            setMessage('Vi fikk ikke kontakt. Sjekk nettet og prøv igjen.')
        }
    }

    if (status === 'sent') {
        return (
            <p className='flex items-center gap-4 text-lg font-semibold' role='status'>
                <span className='flex size-10 items-center justify-center bg-lime text-night'>
                    <Check className='size-5' strokeWidth={2.5} aria-hidden='true' />
                </span>
                Takk! Du er med på listen.
            </p>
        )
    }

    return (
        <form onSubmit={onSubmit} noValidate>
            <label htmlFor={id} className='t-label text-tone-muted'>E-postadresse</label>
            <div className='mt-3 flex flex-col gap-3 sm:flex-row'>
                <input
                    id={id}
                    type='email'
                    autoComplete='email'
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    aria-invalid={status === 'error'}
                    aria-describedby={message ? `${id}-message` : undefined}
                    placeholder='navn@bedrift.no'
                    className='min-h-14 flex-1 border border-tone-line bg-transparent px-4 text-lg placeholder:text-tone-muted/70 focus:border-tone-accent focus:outline-none'
                />
                <button type='submit' disabled={status === 'sending'} className='btn btn-lg disabled:opacity-70'>
                    <span>{status === 'sending' ? 'Sender' : 'Meld meg på'}</span>
                    {status === 'sending'
                        ? <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                        : <ArrowRight className='btn-arrow size-4' aria-hidden='true' />}
                </button>
            </div>

            <div className='hidden' aria-hidden='true'>
                <input tabIndex={-1} autoComplete='off' value={trap} onChange={event => setTrap(event.target.value)} name='website' />
            </div>

            {message && <p id={`${id}-message`} className='mt-3 text-sm font-medium text-tone-ink' role='alert'>{message}</p>}

            <p className='mt-4 text-xs text-tone-muted'>
                Du kan melde deg av når som helst. Les mer i{' '}
                <a href={LINKS.privacy} target='_blank' rel='noopener noreferrer' className='u-link-static'>personvernerklæringen</a>.
            </p>
        </form>
    )
}
